import { WorkspaceTypeID } from '../lib/api/workspaces/dto';

export const WORKSPACE_TYPE_ROUTES = {
  [WorkspaceTypeID.HCPExplorer]: '/hcp',
  [WorkspaceTypeID.HCPExpansion]: '/hcp',
  [WorkspaceTypeID.Throtle]: '/throtle',
  [WorkspaceTypeID.ListMatching]: '/list-matching',
  [WorkspaceTypeID.MediaEffectiveness]: '/media-effectiveness',
  [WorkspaceTypeID.DTC]: '/dtc',
  [WorkspaceTypeID.HCPSiteAnalytics]: '/hcp-site-analytics',
  [WorkspaceTypeID.ListMatchingConsent]: '/list-matching-consent',
  [WorkspaceTypeID.BrandExplorer]: '/brand-explorer',
  [WorkspaceTypeID.MedscapeSoftMatch]: '/medscape-soft-match',
} as const satisfies Record<WorkspaceTypeID, string>;

export const ROUTES = {
  WORKSPACES: '/workspaces',
  QUERY_PARAMS_TEST: '/query-params-test',
  AI_ASSISTANT_DEBUG_TOOL: '/ai-assistant-debug-tool',
  WORKSPACE_NEW: '/workspaces/new',
  WORKSPACE_NEW_ADVERTISER_SELECTION: '/workspaces/new/:workspaceTypeId',
  LINK_MEDIA_EFFECTIVENESS: `/link${WORKSPACE_TYPE_ROUTES[WorkspaceTypeID.MediaEffectiveness]}`,
  LINK_LIST_MATCHING: `/link${WORKSPACE_TYPE_ROUTES[WorkspaceTypeID.ListMatching]}`,
  WORKSPACE_HCP_NEW: `/workspaces${WORKSPACE_TYPE_ROUTES[WorkspaceTypeID.HCPExplorer]}/new`,
  WORKSPACE_HCP_VIEW: `/workspaces${WORKSPACE_TYPE_ROUTES[WorkspaceTypeID.HCPExplorer]}/:workspaceId`,
  WORKSPACE_THROTLE_VIEW: `/workspaces${WORKSPACE_TYPE_ROUTES[WorkspaceTypeID.Throtle]}/:workspaceId`,
  WORKSPACE_LIST_MATCHING_NEW: `/workspaces${WORKSPACE_TYPE_ROUTES[WorkspaceTypeID.ListMatching]}/new/:workspaceId`,
  WORKSPACE_LIST_MATCHING_VIEW: `/workspaces${WORKSPACE_TYPE_ROUTES[WorkspaceTypeID.ListMatching]}/:workspaceId`,
  WORKSPACE_LIST_MATCHING_CONSENT_VIEW: `/workspaces${WORKSPACE_TYPE_ROUTES[WorkspaceTypeID.ListMatchingConsent]}/:workspaceId`,
  WORKSPACE_MEDIA_EFFECTIVENESS_VIEW: `/workspaces${WORKSPACE_TYPE_ROUTES[WorkspaceTypeID.MediaEffectiveness]}/:workspaceId`,
  WORKSPACE_DTC_VIEW: `/workspaces${WORKSPACE_TYPE_ROUTES[WorkspaceTypeID.DTC]}/:workspaceId`,
  WORKSPACE_HCP_SITE_ANALYTICS_VIEW: `/workspaces${WORKSPACE_TYPE_ROUTES[WorkspaceTypeID.HCPSiteAnalytics]}/:workspaceId`,
  WORKSPACE_BRAND_EXPLORER_VIEW: `/workspaces${WORKSPACE_TYPE_ROUTES[WorkspaceTypeID.BrandExplorer]}/:workspaceId`,
  WORKSPACE_MEDSCAPE_SOFT_MATCH_VIEW: `/workspaces${WORKSPACE_TYPE_ROUTES[WorkspaceTypeID.MedscapeSoftMatch]}/:workspaceId`,
} as const;

type QueryParams = Record<string, unknown>;

export interface ListMatchingQueryParams {
  npiListID?: string | null;
  advertiserID?: string | null;
  accountID?: string | null;
}

export const buildQueryString = (params: QueryParams): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

export const buildPath = (basePath: string, params?: QueryParams): string => {
  const queryString = params ? buildQueryString(params) : '';
  return `${basePath}${queryString}`;
};

export const buildCreateListMatchingWorkspacePath = (
  workspaceId: string | number,
  queryParams?: ListMatchingQueryParams,
): string => {
  const basePath = `${ROUTES.WORKSPACES}${WORKSPACE_TYPE_ROUTES[WorkspaceTypeID.ListMatching]}/new/${workspaceId}`;
  return buildPath(basePath, queryParams as QueryParams);
};

export const getWorkspacePath = (workspaceId: string | number, workspaceTypeId: WorkspaceTypeID) => {
  const typeRoute = WORKSPACE_TYPE_ROUTES[workspaceTypeId];

  if (!typeRoute) {
    throw new Error('Workspace type is not supported.');
  }

  return `${ROUTES.WORKSPACES}${typeRoute}/${workspaceId}`;
};

export const isListMatchingWorkspace = (workspaceTypeId: WorkspaceTypeID): boolean => {
  return workspaceTypeId === WorkspaceTypeID.ListMatching;
};

export const isHcpExpansionWorkspace = (workspaceTypeId: WorkspaceTypeID): boolean => {
  return workspaceTypeId === WorkspaceTypeID.HCPExpansion;
};

export const isHcpExplorerWorkspace = (workspaceTypeId: WorkspaceTypeID): boolean => {
  return workspaceTypeId === WorkspaceTypeID.HCPExplorer;
};

export const isThrotleWorkspace = (workspaceTypeId: WorkspaceTypeID): boolean => {
  return workspaceTypeId === WorkspaceTypeID.Throtle;
};

export const isDtcWorkspace = (workspaceTypeId: WorkspaceTypeID): boolean => {
  return workspaceTypeId === WorkspaceTypeID.DTC;
};

export const isMediaEffectivenessWorkspace = (workspaceTypeId: WorkspaceTypeID): boolean => {
  return workspaceTypeId === WorkspaceTypeID.MediaEffectiveness;
};

export const isBrandExplorerWorkspace = (workspaceTypeId: WorkspaceTypeID): boolean => {
  return workspaceTypeId === WorkspaceTypeID.BrandExplorer;
};
